import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Sheet, Stack, Typography } from '@mui/joy';
import { useTheme } from '@mui/joy/styles';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';

import { getSevaGradientConfig, getSevaGradientSurface } from '../theme/branding';

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const gradientSurface = getSevaGradientSurface(theme, { withShadow: true });
  const gradientTokens = getSevaGradientConfig(theme.palette.mode);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.body',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 3, md: 6 },
      }}
    >
      <Stack spacing={3} sx={{ width: '100%', maxWidth: 640 }}>
        <Sheet
          variant="outlined"
          sx={{
            borderRadius: 'xl',
            p: { xs: 3, md: 4 },
            gap: 2.5,
            position: 'relative',
            overflow: 'hidden',
            background:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(150deg, rgba(20,24,34,0.94) 0%, rgba(29,36,52,0.84) 100%)'
                : 'linear-gradient(150deg, rgba(255,255,255,0.98) 0%, rgba(240,247,255,0.88) 100%)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(70% 90% at 20% 15%, rgba(13,109,219,0.18) 0%, transparent 60%), radial-gradient(75% 80% at 85% 85%, rgba(255,177,63,0.2) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />

          <Stack spacing={1.5} sx={{ alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
            <Chip
              size="sm"
              variant="soft"
              color="primary"
              startDecorator={<TravelExploreRoundedIcon fontSize="small" />}
              sx={{ fontWeight: 600 }}
            >
              Page not found
            </Chip>
            <Typography level="h2" sx={{ fontWeight: 800, letterSpacing: -0.3 }}>
              The page you seek has wandered elsewhere
            </Typography>
            <Typography level="body-md" sx={{ color: 'text.secondary', maxWidth: 520 }}>
              Perhaps the seva you are looking for has been moved or renamed. Let’s guide you back to a familiar space so your
              offerings can continue.
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
            <Button
              size="md"
              variant="solid"
              color="primary"
              startDecorator={<HomeRoundedIcon />}
              onClick={() => navigate('/')}
              sx={{ flex: 1 }}
            >
              Return to dashboard
            </Button>
            <Button
              size="md"
              variant="plain"
              color="neutral"
              startDecorator={<SupportAgentRoundedIcon />}
              component="a"
              href="mailto:support@iskconkanpur.org?subject=Missing%20page"
              sx={{ flex: 1 }}
            >
              Contact support team
            </Button>
          </Stack>
        </Sheet>

        <Sheet
          variant="soft"
          color="primary"
          sx={{
            ...gradientSurface,
            color: gradientTokens.muted,
            borderRadius: 'xl',
            p: { xs: 2.5, md: 3 },
            boxShadow: gradientSurface.boxShadow,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography level="title-sm" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Devotional suggestion
          </Typography>
          <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.86)' }}>
            Visit the dashboard to catch up on today’s darshan, seva invitations, and heartfelt announcements. Sri Krishna is
            always revealing new opportunities to serve.
          </Typography>
        </Sheet>
      </Stack>
    </Box>
  );
};

export default NotFound;
